import { ChevronRight, Person } from '@mui/icons-material';
import { Avatar, Box, Button, Container, FormControlLabel, Grid, List, ListItem, ListItemButton, ListItemText, Switch, Typography } from '@mui/material';
import { AppModels, AppState, ContactAttrs, FileInfo, Lang, contactEditorSlice, contactSelector, contactValidator, documentSlice, hasErrors, notEmptyString, saveModel, selectRelatedCompanies, selectRelatedProjects } from 'cdx-common';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { MainViewLoader } from '../../libraries/MainViewLoader';
import { useToaster } from '../../libraries/Toaster';
import { appStore } from '../../store';
import { AppItem, FileLoader } from '../AppItems';
import { LocationInput } from '../location/LocationInput';
import { MasterViewPage } from '../MasterPage';
import { ModelTextfield } from '../utils/ModelTextField';
import { PhoneInput } from '../utils/PhoneInput';
import { AWSImageButtonUploader } from '../utils/Uploader';

export const Contact = () => {
    const params = useParams();
    return <MasterViewPage>
        <MainViewLoader
            selector={contactSelector}
            modelId={params.id ?? '0'}
            onModelLoaded={(contact: ContactAttrs) => {
                appStore.dispatch(documentSlice.actions.setTitle(contact.name));
                appStore.dispatch(contactEditorSlice.actions.init({ ...contact }));
            }}
        >
            <LoadedContact />
        </MainViewLoader>
    </MasterViewPage>;
};


export const LoadedContact = () => {

    const contact = useSelector((state: AppState) => state.contactEditor.contact);
    const relatedCompanies = useSelector((state: AppState) => selectRelatedCompanies(state, contact.id));
    const relatedProjects = useSelector((state: AppState) => selectRelatedProjects(state, contact.id));
    const storedContact = useSelector((state: AppState) => contactSelector.selectById(state, contact.id));
    const { toast } = useToaster();
    const update = (p: Partial<ContactAttrs>) => {
        appStore.dispatch(contactEditorSlice.actions.updateCOntact(p));
    };

    const onSaveClick = async () => {
        const validatedContact = await contactValidator.validate(contact);
        appStore.dispatch(contactEditorSlice.actions.updateCOntact(validatedContact));
        if (!hasErrors(validatedContact)) {
            const newContact = await saveModel(AppModels.contacts, validatedContact);
            appStore.dispatch(contactEditorSlice.actions.updateCOntact(newContact));
            if (!hasErrors(newContact)) {
                appStore.dispatch(contactEditorSlice.actions.init(newContact));
                toast.success('Modifications enregistrées');
            }
        }
    };

    if (!storedContact) return <></>;

    const onImageUpload = async (fileInfo: FileInfo) => {
        await saveModel(AppModels.contacts, {
            id: storedContact?.id,
            image: fileInfo
        });
    };

    return <Container>
        <Grid container spacing={1} padding={1}>
            <Grid item xs={12}>
                <Grid container sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Grid item sx={{ width: 100, height: 100, display: 'flex', position: 'relative', alignItems: 'center' }}>
                        {storedContact.imageFileId ? <FileLoader id={storedContact.imageFileId} render={file => <Avatar sx={{ width: '100%', height: '100%' }} src={file.url} />} /> : <Avatar sx={{ width: '100%', height: '100%' }}><Person /></Avatar>}
                        <div className="appearOnParentHover" style={{ textAlign: 'center', position: 'absolute', bottom: 2, width: '100%' }}>
                            <AWSImageButtonUploader onUpload={onImageUpload} variant='contained' size='small' />
                        </div>
                    </Grid>
                    <Grid item sx={{ flexGrow: 1, p: 2 }}>
                        <Typography variant='h3'>{storedContact.name}</Typography>
                        {storedContact.title ? <Typography variant='subtitle1'>{storedContact.title}</Typography> : null}
                    </Grid>
                </Grid>

            </Grid>
            <Grid item xs={12} sm={6} lg={6} bgcolor='white' padding={2}>
                <Typography gutterBottom variant='h4'>Infos et coordonnées</Typography>
                <ContactForm contact={contact} update={update} />
                <Grid container spacing={2} columns={2}>
                    <Grid item xs={2} alignContent='center'>
                        <Button variant='contained' color='primary' onClick={onSaveClick}>Sauvegarder</Button>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} sm={6} bgcolor='white'>
                <Typography variant='h4'>Compagnie(s)</Typography>
                <List>
                    {relatedCompanies.length === 0 ? <ListItem><ListItemText primary='Aucune' /></ListItem> : null}
                    {relatedCompanies.map(rc => <ListItem
                        secondaryAction={<ChevronRight />}>
                        <ListItemButton component={Link} to={'/company/' + rc.relatedId}>
                            <ListItemText
                                primary={<AppItem mode='compact' type={AppModels.companies} id={rc.relatedId} />}
                                secondary={rc.label}
                            />
                        </ListItemButton>
                    </ListItem>

                    )}
                </List>
                <Typography variant='h4'>Projet(s)</Typography>
                <List>
                    {relatedProjects.length === 0 ? <ListItem><ListItemText primary='Aucun' /></ListItem> : null}
                    {relatedProjects.map(rp => <ListItem>
                        <ListItemButton component={Link} to={'/project/' + rp.relatedId}>
                            <ListItemText
                                primary={<AppItem mode='compact' type={AppModels.projects} id={rp.relatedId} />}
                                secondary={rp.label}
                            />
                        </ListItemButton>
                    </ListItem>
                    )}
                </List>
            </Grid>
        </Grid>
    </Container>;
};

export const ContactForm = (props: { contact: ContactAttrs, update: (p: Partial<ContactAttrs>) => void, compact?: boolean; }) => {
    return <Grid container spacing={2} columns={2}>
        <Grid item xs={2} md={props.compact ? 2 : 1}>
            <ModelTextfield
                fullWidth
                model={props.contact}
                row='firstName'
                modelType={AppModels.contacts}
                onChange={(event) => props.update({ firstName: event.target.value })} />
        </Grid>
        <Grid item xs={2} md={props.compact ? 2 : 1}>
            <ModelTextfield
                fullWidth
                model={props.contact}
                row='lastName'
                modelType={AppModels.contacts}
                onChange={(event) => props.update({ lastName: event.target.value })} />
        </Grid>
        <Grid item xs={2}>
            <ModelTextfield
                fullWidth
                model={props.contact}
                row='title'
                modelType={AppModels.contacts}
                onChange={(event) => props.update({ title: event.target.value })} />
        </Grid>
        <Grid item xs={2}>
            <ModelTextfield
                fullWidth
                type='email'
                model={props.contact}
                row='email'
                modelType={AppModels.contacts}
                onChange={(event) => props.update({ email: event.target.value })} />
        </Grid>
        <Grid item xs={2}>
            <LocationInput fullWidth value={props.contact.locationId} onChange={(locationId: ModelId | null) => { props.update({ locationId }); }} />
        </Grid>
        <Grid item xs={2} md={2}>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box sx={{ flexGrow: 1 }}>
                    <PhoneInput
                        fullWidth
                        label={Lang('labels.contacts.tel')}
                        value={props.contact.tel ?? ''}
                        error={!!props.contact.__errors?.tel}
                        helperText={props.contact.__errors?.tel}
                        onChange={(numericPhone: string | null) => {
                            props.update({ tel: numericPhone });
                        }}
                    />
                </Box>
                <Box sx={{ width: 110 }}>
                    <ModelTextfield
                        sx={{ width: 100 }}
                        model={props.contact}
                        row='telPoste'
                        modelType={AppModels.contacts}
                        onChange={(event) => props.update({ telPoste: notEmptyString(event.target.value) })} />
                </Box>
            </Box>
        </Grid>
        <Grid item xs={2} md={2}>
            <PhoneInput
                fullWidth
                label={Lang('labels.contacts.cell')}
                value={props.contact.cell}
                error={!!props.contact.__errors?.cell}
                helperText={props.contact.__errors?.cell}
                onChange={(numericValue) => props.update({ cell: numericValue })} />
        </Grid>
        <Grid item xs={2}>
            <FormControlLabel control={<Switch checked={props.contact.onSiteContact === true} onChange={() => {
                props.update({ onSiteContact: !props.contact.onSiteContact });
            }} />} label="Contact de chantier" />
            <Typography variant='caption'>Coordonnées dévoilées aux employés de chantier.</Typography>
        </Grid>
    </Grid>;
};
